import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  ShoppingBagIcon,
  ClockIcon,
  CheckCircleIcon,
  TruckIcon,
  XCircleIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const { formatPrice } = useCurrency();
  const { isAuthenticated, getToken } = useAuth();
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL || '';

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken(false); // E-commerce token
      if (!token) {
        navigate('/login');
        return;
      }

      console.log('📦 [Orders] Fetching orders, page:', pagination.page);
      const response = await fetch(`${API_BASE}/api/orders?page=${pagination.page}&limit=${pagination.limit}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('📦 [Orders] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Orders] API Error:', response.status, errorText);
        toast.error(`Failed to fetch orders: ${response.status}`);
        return;
      }

      const data = await response.json();
      console.log('📦 [Orders] Orders received:', data.data?.orders?.length || 0);
      
      if (data.success) {
        const ordersArray = Array.isArray(data.data.orders) ? data.data.orders : [];
        setOrders(ordersArray);
        setPagination(prev => ({
          ...prev,
          total: data.data.pagination?.total || 0,
          pages: data.data.pagination?.pages || 0
        }));
        console.log('✅ [Orders] Orders loaded successfully:', ordersArray.length);
      } else {
        console.error('❌ [Orders] API returned success=false:', data.message);
        toast.error(data.message || 'Failed to fetch orders');
      }
    } catch (error) {
      console.error('❌ [Orders] Error fetching orders:', error);
      toast.error('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, getToken, navigate, pagination.limit, pagination.page]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [fetchOrders, isAuthenticated]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="w-5 h-5 text-yellow-600" />;
      case 'confirmed':
      case 'processing':
        return <CheckCircleIcon className="w-5 h-5 text-blue-600" />;
      case 'shipped':
        return <TruckIcon className="w-5 h-5 text-indigo-600" />;
      case 'delivered':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      case 'cancelled':
        return <XCircleIcon className="w-5 h-5 text-red-600" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    const statusUpper = (status || '').toUpperCase();
    switch (statusUpper) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'CONFIRMED':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'PROCESSING':
        return 'bg-cyan-100 text-cyan-800 border-cyan-300';
      case 'PACKING':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'SHIPPED':
      case 'DISPATCHED':
        return 'bg-indigo-100 text-indigo-800 border-indigo-300';
      case 'OUT_FOR_DELIVERY':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'DELIVERED':
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'CANCELLED':
      case 'RETURNED':
      case 'REFUNDED':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusTimeline = (status) => {
    const statusUpper = (status || 'PENDING').toUpperCase();
    // Include all possible statuses in order
    const statuses = ['PENDING', 'CONFIRMED', 'PROCESSING', 'PACKING', 'SHIPPED', 'DISPATCHED', 'OUT_FOR_DELIVERY', 'DELIVERED', 'COMPLETED'];
    const currentIndex = statuses.indexOf(statusUpper);
    const isCancelled = statusUpper === 'CANCELLED' || statusUpper === 'RETURNED' || statusUpper === 'REFUNDED';
    
    // Map status to display labels
    const statusLabels = {
      'PENDING': 'Order Placed',
      'CONFIRMED': 'Confirmed',
      'PROCESSING': 'Processing',
      'PACKING': 'Packing',
      'SHIPPED': 'Shipped',
      'DISPATCHED': 'Dispatched',
      'OUT_FOR_DELIVERY': 'Out for Delivery',
      'DELIVERED': 'Delivered',
      'COMPLETED': 'Completed'
    };
    
    return statuses.map((s, index) => {
      const isCompleted = index <= currentIndex && currentIndex >= 0 && !isCancelled;
      const isCurrent = index === currentIndex && !isCancelled;
      
      return {
        status: s,
        label: statusLabels[s] || s.replace(/_/g, ' '),
        isCompleted: isCompleted,
        isCurrent: isCurrent,
        isCancelled: isCancelled
      };
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin absolute top-0 left-0 animate-pulse"></div>
          </div>
          <p className="mt-4 text-gray-600">Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">My Orders</h1>
          <p className="text-gray-600">View and track all your orders</p>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <ShoppingBagIcon className="w-24 h-24 text-gray-300 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Orders Yet</h2>
            <p className="text-gray-600 mb-8">Start shopping to see your orders here!</p>
            <Link
              to="/products"
              className="inline-block bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-8 py-4 rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg font-semibold"
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <>
            {/* Orders List */}
            <div className="space-y-4 mb-8">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      {/* Order Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">
                            Order #{order.order_number || order.id}
                          </h3>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold border flex items-center gap-1 ${getStatusColor(
                              order.status
                            )}`}
                          >
                            {getStatusIcon(order.status)}
                            {(order.status || 'PENDING').replace(/_/g, ' ')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          Placed on {formatDate(order.created_at)}
                        </p>
                        <p className="text-sm text-gray-600 mb-3">
                          {order.item_count || 0} {(order.item_count || 0) === 1 ? 'item' : 'items'}
                        </p>
                        {/* Status Timeline */}
                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                          {getStatusTimeline(order.status).slice(0, 5).map((step, idx) => (
                            <div key={idx} className="flex items-center gap-1">
                              <div
                                className={`w-2 h-2 rounded-full transition-all ${
                                  step.isCompleted
                                    ? 'bg-green-500'
                                    : step.isCurrent
                                    ? 'bg-yellow-500 animate-pulse'
                                    : 'bg-gray-300'
                                }`}
                                title={step.label}
                              />
                              {idx < 4 && (
                                <div
                                  className={`w-4 h-0.5 transition-all ${
                                    step.isCompleted ? 'bg-green-500' : 'bg-gray-300'
                                  }`}
                                />
                              )}
                            </div>
                          ))}
                          <span className="text-xs text-gray-500 ml-2">
                            {getStatusTimeline(order.status).find(s => s.isCurrent)?.label || 
                             (order.status?.toUpperCase() === 'CANCELLED' ? 'Cancelled' : 
                              order.status?.toUpperCase() === 'RETURNED' ? 'Returned' :
                              order.status?.toUpperCase() === 'REFUNDED' ? 'Refunded' :
                              'Pending')}
                          </span>
                        </div>
                      </div>

                      {/* Order Total */}
                      <div className="text-right">
                        <p className="text-2xl font-bold text-indigo-600 mb-2">
                          {formatPrice(order.total_amount || 0)}
                        </p>
                        <Link
                          to={`/orders/${order.id}`}
                          className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-100 transition-colors font-semibold text-sm"
                        >
                          <EyeIcon className="w-4 h-4" />
                          View Details
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-gray-700 font-semibold">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.pages}
                  className="px-4 py-2 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Orders;
